import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import {
  useClassroom,
  useClassroomLesson,
  signedClassroomFileUrl,
  formatBytes,
  type ClassroomFile,
} from "@/hooks/useClassroom";
import MembersOnlyBox from "@/components/MembersOnlyBox";
import { ArrowLeft, ArrowRight, Download, ExternalLink, Lock, PlayCircle } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { SITE } from "@/data/site";
import { cleanSummary, gatedDescription } from "@/lib/summary";

/** Same Markdown styling as PackDetail (no typography plugin). */
const PROSE =
  "text-[15px] leading-7 text-foreground/90 " +
  "[&_h1]:mt-10 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:text-foreground " +
  "[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-foreground " +
  "[&_h3]:mt-7 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground " +
  "[&_p]:my-4 [&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline " +
  "[&_img]:my-6 [&_img]:rounded-lg [&_img]:border [&_img]:border-border " +
  "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 " +
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] " +
  "[&_pre]:my-5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-border [&_pre]:bg-muted [&_pre]:p-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0 " +
  "[&_table]:my-5 [&_table]:w-full [&_table]:text-sm [&_th]:border-b [&_th]:border-border [&_th]:py-2 [&_th]:pr-4 [&_th]:text-left [&_th]:font-semibold [&_td]:border-b [&_td]:border-border/60 [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top " +
  "[&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-primary/60 [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground " +
  "[&_hr]:my-8 [&_hr]:border-border";

const FileButton = ({ file }: { file: ClassroomFile }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (file.kind !== "file" && file.external_url) {
    const isSkool = file.kind === "skool";
    return (
      <a href={file.external_url} target="_blank" rel="noopener noreferrer">
        <Button variant="outline" className="w-full justify-between">
          <span className="truncate">{file.label}</span>
          {isSkool ? <PlayCircle className="w-4 h-4 shrink-0" /> : <ExternalLink className="w-4 h-4 shrink-0" />}
        </Button>
      </a>
    );
  }

  const open = async () => {
    if (!file.storage_path) return;
    setBusy(true);
    try {
      const url = await signedClassroomFileUrl(file.storage_path, 120);
      window.location.assign(url);
    } catch (e) {
      toast({
        title: "Could not open that file",
        description: (e as Error).message || "Your access could not be verified. Sign in again and retry.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button className="w-full justify-between" onClick={open} disabled={busy}>
      <span className="truncate">{file.label}</span>
      <span className="flex items-center gap-2 text-xs opacity-80 shrink-0">
        {file.size_bytes ? formatBytes(file.size_bytes) : null}
        <Download className="w-4 h-4" />
      </span>
    </Button>
  );
};

const ClassroomLesson = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useClassroomLesson(slug);
  const { data: index } = useClassroom();
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, loading: accessLoading } = usePaymentStatus();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-32 text-center">
          <p className="text-muted-foreground mb-6">That lesson does not exist, or is no longer in the classroom.</p>
          <Link to="/classroom">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4" /> Classroom Library
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { lesson, course, files, bodyMd } = data;
  const gateLoading = authLoading || accessLoading;
  const isMember = !!user && hasAccess;
  const siblings = (index?.lessons ?? [])
    .filter((l) => l.course_id === lesson.course_id && l.section === lesson.section && l.slug !== lesson.slug)
    .slice(0, 3);

  const publicSummary = cleanSummary(lesson.summary === lesson.title ? "" : lesson.summary, 600);

  /**
   * Article schema on a gated page is legitimate as long as the summary really is
   * public, which it is — `skool_lessons` is readable by anyone and only
   * `skool_lesson_bodies` is behind RLS. isAccessibleForFree + hasPart is exactly the
   * pattern Google documents for paywalled content, and lying about it is how a site
   * earns a manual action.
   */
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${SITE.origin}/classroom/${lesson.slug}#article`,
      headline: lesson.title,
      description: publicSummary || undefined,
      url: `${SITE.origin}/classroom/${lesson.slug}`,
      datePublished: lesson.last_changed_at,
      dateModified: lesson.last_changed_at,
      author: { "@id": `${SITE.origin}/#hector-diaz` },
      publisher: { "@id": `${SITE.origin}/#organization` },
      isPartOf: { "@id": `${SITE.origin}/#website` },
      inLanguage: "en",
      isAccessibleForFree: bodyMd !== null,
      ...(bodyMd === null
        ? {
            hasPart: {
              "@type": "WebPageElement",
              isAccessibleForFree: false,
              cssSelector: ".gated-body",
            },
          }
        : {}),
      ...(course ? { articleSection: course.title } : {}),
    },
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Classroom Library", path: "/classroom" },
      { name: lesson.title, path: `/classroom/${lesson.slug}` },
    ]),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={lesson.title}
        description={gatedDescription(lesson.summary === lesson.title ? "" : lesson.summary, {
          author: SITE.founder,
          fallback: `${lesson.title} — a lesson in the AI Automation Base Classroom Library, mirrored from the Skool classroom.`,
        })}
        path={`/classroom/${lesson.slug}`}
        type="article"
        publishedTime={lesson.last_changed_at}
        modifiedTime={lesson.last_changed_at}
        author={SITE.founder}
        jsonLd={jsonLd}
      />
      <main className="container mx-auto px-4 pt-24 pb-20 max-w-5xl">
        <Link to="/classroom" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Classroom Library
        </Link>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
            {course ? <Badge variant="secondary">{course.title}</Badge> : null}
            {lesson.section ? <span className="text-muted-foreground">{lesson.section}</span> : null}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-foreground">{lesson.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <a href={lesson.skool_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
              Open on Skool <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span>By {SITE.founder}</span>
            <span aria-hidden>·</span>
            <time dateTime={lesson.last_changed_at}>
              Updated {new Date(lesson.last_changed_at).toLocaleDateString()}
            </time>
          </div>
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article>
            {gateLoading ? (
              <p className="text-muted-foreground">Checking access...</p>
            ) : bodyMd !== null ? (
              bodyMd.trim() ? (
                <div className={PROSE}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMd}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-muted-foreground">This lesson is just its files and links — see the panel.</p>
              )
            ) : (
              <div className="gated-body">
                {/* Was the raw mirrored teaser, which ends mid-clause on an ellipsis.
                    A model will not quote a fragment, so it is trimmed to whole
                    sentences before it is shown or put into the Article description. */}
                {publicSummary ? (
                  <p className="text-[15px] leading-7 text-foreground/90">{publicSummary}</p>
                ) : null}
                <div className="mt-6 rounded-lg border border-dashed border-border p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                    <Lock className="w-4 h-4 text-primary" /> The full write-up is for members
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Vault members and members of AI Automations by Hector on Skool can read every lesson here and
                    download the files.
                  </p>
                </div>
              </div>
            )}
          </article>

          <aside className="lg:sticky lg:top-24 self-start">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground mb-1">Files &amp; links</h2>
              <p className="text-xs text-muted-foreground mb-4">Mirrored from the Skool classroom. Members only.</p>

              {gateLoading ? (
                <div className="text-sm text-muted-foreground">Checking access...</div>
              ) : isMember ? (
                <div className="space-y-2">
                  {files.map((f) => (
                    <FileButton key={f.id} file={f} />
                  ))}
                  {files.length === 0 ? <p className="text-sm text-muted-foreground">Nothing attached to this lesson.</p> : null}
                </div>
              ) : (
                <MembersOnlyBox user={user} items={files.map((f) => f.label)} />
              )}
            </div>
          </aside>
        </div>

        {siblings.length ? (
          <section className="mt-16">
            <h2 className="text-xl font-semibold text-foreground mb-4">More in {lesson.section || "this course"}</h2>
            <div className="grid gap-5 sm:grid-cols-3">
              {siblings.map((l) => (
                <Link key={l.slug} to={`/classroom/${l.slug}`} className="rounded-lg border border-border bg-card p-4 hover:border-primary/60">
                  <div className="font-medium text-foreground leading-snug">{l.title}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-primary">
                    Open <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
};

export default ClassroomLesson;
